import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Date adapter that builds, reads, and renders every Material datepicker/calendar date in UTC.
 *
 * Date-only fields are stored as UTC-midnight ISO strings (e.g. "2026-04-02T00:00:00.000Z").
 * The stock NativeDateAdapter works in local time, so in non-UTC offsets the picked/displayed
 * day shifts. Anchoring the whole adapter to UTC keeps the calendar day stable everywhere.
 */
@Injectable()
export class UtcDateAdapter extends NativeDateAdapter {
  override getYear(date: Date): number {
    return date.getUTCFullYear();
  }

  override getMonth(date: Date): number {
    return date.getUTCMonth();
  }

  override getDate(date: Date): number {
    return date.getUTCDate();
  }

  override getDayOfWeek(date: Date): number {
    return date.getUTCDay();
  }

  override getYearName(date: Date): string {
    return new Intl.DateTimeFormat(this.locale, { year: 'numeric', timeZone: 'UTC' }).format(date);
  }

  override getNumDaysInMonth(date: Date): number {
    return this._createUtcDate(this.getYear(date), this.getMonth(date) + 1, 0).getUTCDate();
  }

  override createDate(year: number, month: number, date: number): Date {
    if (month < 0 || month > 11) {
      throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
    }
    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }

    const result = this._createUtcDate(year, month, date);

    if (result.getUTCMonth() !== month) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }

    return result;
  }

  override today(): Date {
    const now = new Date();
    return this._createUtcDate(now.getFullYear(), now.getMonth(), now.getDate());
  }

  override parse(value: any, parseFormat?: any): Date | null {
    const parsed = super.parse(value, parseFormat);
    if (parsed && this.isValid(parsed)) {
      return this._createUtcDate(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return parsed;
  }

  override format(date: Date, displayFormat: object): string {
    if (!this.isValid(date)) {
      throw Error('UtcDateAdapter: Cannot format invalid date.');
    }
    return new Intl.DateTimeFormat(this.locale, { ...displayFormat, timeZone: 'UTC' }).format(date);
  }

  override addCalendarMonths(date: Date, months: number): Date {
    let newDate = this._createUtcDate(this.getYear(date), this.getMonth(date) + months, this.getDate(date));

    // Roll back to the last day of the desired month when the original day overflows a shorter month.
    if (this.getMonth(newDate) !== (((this.getMonth(date) + months) % 12) + 12) % 12) {
      newDate = this._createUtcDate(this.getYear(newDate), this.getMonth(newDate), 0);
    }

    return newDate;
  }

  override addCalendarDays(date: Date, days: number): Date {
    return this._createUtcDate(this.getYear(date), this.getMonth(date), this.getDate(date) + days);
  }

  /** Builds a UTC-midnight date, allowing month/day overflow (mirrors the base helper in UTC). */
  private _createUtcDate(year: number, month: number, date: number): Date {
    // setUTCFullYear avoids the constructor's <100 year => 19xx conversion.
    const result = new Date(0);
    result.setUTCFullYear(year, month, date);
    result.setUTCHours(0, 0, 0, 0);
    return result;
  }
}
